import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

import config from '../../config';

export class MemberApi {

  /** @type {AxiosInstance} */
  axios;

  /** @type {string} */
  refreshToken;

  /** @type {string} */
  language;

  constructor(url) {
    this.axios = axios.create({
      baseURL: url || config.api,
      headers: {
        'X-APP-PLATFORM': 'web',
        'X-APP-VERSION': '1.0.0',
        'X-OS-Version': this.getOSVersion()
      }
    });

    // Add a request interceptor
    this.axios.interceptors.request.use((config) => {
      config.headers['X-REQUEST-ID'] = uuidv4();
      return config;
    });
  }

  /**
   * Return current device os name and version
   */
  getOSVersion() {
    const userAgent = navigator.userAgent;
    let osVersion = 'Unknown OS';

    if (/Windows NT 10.0/.test(userAgent)) osVersion = 'Windows 10';
    else if (/Windows NT 6.3/.test(userAgent)) osVersion = 'Windows 8.1';
    else if (/Windows NT 6.2/.test(userAgent)) osVersion = 'Windows 8';
    else if (/Windows NT 6.1/.test(userAgent)) osVersion = 'Windows 7';
    else if (/Windows NT 6.0/.test(userAgent)) osVersion = 'Windows Vista';
    else if (/Windows NT 5.1/.test(userAgent)) osVersion = 'Windows XP';
    else if (/Mac OS X 10[._]\d+/.test(userAgent)) {
      const match = userAgent.match(/Mac OS X 10[._]\d+/);
      osVersion = match ? match[0].replace(/_/g, '.') : 'Mac OS';
    } else if (/Android \d+/.test(userAgent)) {
      const match = userAgent.match(/Android \d+/);
      osVersion = match ? match[0] : 'Android';
    } else if (/Linux/.test(userAgent)) osVersion = 'Linux';
    else if (/iPhone OS \d+/.test(userAgent)) {
      const match = userAgent.match(/iPhone OS \d+/);
      osVersion = match ? match[0].replace(/_/g, '.') : 'iOS';
    } else if (/iPad OS \d+/.test(userAgent)) {
      const match = userAgent.match(/iPad OS \d+/);
      osVersion = match ? match[0].replace(/_/g, '.') : 'iOS';
    }

    return osVersion;
  }

  async login({ username, password = null, otp = null, grantType = 'password', clientId, gRecaptchaResponse = null }) {
    const endpoint = '../../oauth2/token';

    const bodyParams = new URLSearchParams();
    bodyParams.append('grant_type', grantType);
    bodyParams.append('client_id', clientId);
    bodyParams.append('username', username);
    if (password) bodyParams.append('password', password);
    if (otp) bodyParams.append('password', otp);
    if (gRecaptchaResponse) bodyParams.append('g-recaptcha-response', gRecaptchaResponse);

    const response = await this.axios.post(endpoint, bodyParams);
    this.setToken(response.data);
    localStorage.setItem('token_type', response.data.token_type);
    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);

    return response.data;
  }

  logout(afterLogout = () => {}) {
    localStorage.removeItem('token_type');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('wallet_token');
    delete(this.axios.defaults.headers.common.Authorization);
    this.refreshToken = null;
    afterLogout();
  }

  async checkExist({ email, countryCode, mobile, gRecaptchaResponse = null }) {
    const query = {
      email,
      country_code: countryCode,
      mobile
    };
    if (gRecaptchaResponse) query['g-recaptcha-response'] = gRecaptchaResponse;
    const response = await this.axios.get('/user/check', { params: query });
    return response?.data?.data?.status;
  }

  async checkForgotEmail({ countryCode, mobile, first_name, last_name }) {
    const query = {
      country_code: countryCode,
      mobile,
      first_name,
      last_name
    };
    const response = await this.axios.get('/user/check-forgot-email', { params: query });
    return response?.data?.data?.email;
  }

  async requestOtp({ email, countryCode, mobile, gRecaptchaResponse = null }) {
    const postData = {
      email,
      country_code: countryCode,
      mobile
    };
    if (gRecaptchaResponse) postData['g-recaptcha-response'] = gRecaptchaResponse;
    await this.axios.post('/user/request-otp', postData);
  }

  async requestPasswordUser({ email, countryCode, mobile }) {
    const response = await this.axios.post('/user/request-password-user', {
      email, country_code: countryCode, mobile
    });

    return response?.data?.data;
  }

  async forgotPassword({ email, countryCode, mobile, gRecaptchaResponse = null }) {
    const postData = {
      email,
      country_code: countryCode,
      mobile
    };
    if (gRecaptchaResponse) postData['g-recaptcha-response'] = gRecaptchaResponse;
    const response = await this.axios.post('/user/forgot-password', postData);
    return response?.data?.data;
  }

  async resetPassword({ email, countryCode, mobile, otp, password }) {
    const response = await this.axios.put('/user/reset-password', {
      email, country_code: countryCode, mobile, otp, password
    });
    return response?.status === 202;
  }

  async walletLogin({ hashToken, clientId, grantType = 'login_wallet', gRecaptchaResponse = null }) {
    const endpoint = '../../oauth2/token';

    const bodyParams = new URLSearchParams();
    bodyParams.append('grant_type', grantType);
    bodyParams.append('client_id', clientId);
    bodyParams.append('hash_token', hashToken);
    if (gRecaptchaResponse) bodyParams.append('g-recaptcha-response', gRecaptchaResponse);
    const response = await this.axios.post(endpoint, bodyParams);
    this.setToken(response.data);
    localStorage.setItem('token_type', response.data.token_type);
    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);

    return response?.data;
  }

  async refresh({ clientId }) {
    const bodyParams = new URLSearchParams();
    bodyParams.append('grant_type', 'refresh_token');
    bodyParams.append('client_id', clientId);
    bodyParams.append('refresh_token', this.refreshToken);

    const response = await this.axios.post('../../oauth2/token', bodyParams);
    this.setToken(response.data);
    return response?.data;
  }

  async getToken(token) {
    this.axios.defaults.headers.common.Authorization = `Bearer ${token}`;

    const response = await this.axios.get('../../oauth2/tokeninfo');
    this.setToken(response.data);
    return response.data;
  }

  setToken = (auth) => {
    this.axios.defaults.headers.common.Authorization = `${auth.token_type} ${
      auth.access_token
    }`;
    this.refreshToken = auth.refresh_token;
  };

  setLanguage = (lang) => {
    this.axios.defaults.headers.common['Accept-Language'] = lang;
  };

  async getAppConfig() {
    const response = await this.axios.get('/app/config');

    return response.data.data;
  }

  async getProgram() {
    const response = await this.axios.get('/program');

    return response.data.data;
  }

  async getLandingPopup() {
    const response = await this.axios.get('/landing-popup');

    return response.data.data;
  }

  async getPage({ ruri }) {
    const response = await this.axios.get(`/pages?ruri=${ruri}`);

    return response.data.data;
  }

  async getMe() {
    const response = await this.axios.get('/me');

    return response.data.data;
  }

  async createLoyaltyAccount(accountFields, gRecaptchaResponse = null) {
    const bodyParams = new URLSearchParams();
    Object.keys(accountFields).forEach((key) => {
      bodyParams.append(key, accountFields[key]);
    });
    if (gRecaptchaResponse) bodyParams.append('g-recaptcha-response', gRecaptchaResponse);
    const response = await this.axios.post('/me/loyalty-account', bodyParams);

    return response.data.data;
  }

  async updateLoyaltyAccount(accountFields) {
    const bodyParams = new URLSearchParams();
    Object.keys(accountFields).forEach((key) => {
      bodyParams.append(key, accountFields[key]);
    });
    const response = await this.axios.put('/me/loyalty-account', bodyParams);
    return response.status === 202;
  }

  async getCardInfo() {
    const response = await this.axios.get('/me/loyalty-card');

    return response.data.data;
  }

  async getTierMission() {
    const response = await this.axios.get('/me/loyalty-missions/tier');

    return response.data.data;
  }

  async getMissions({ types }) {
    const query = {};
    if (types && types !== 'null') query.types = Array.isArray(types) ? types.join(',') : types;
    const response = await this.axios.get('/me/loyalty-missions', { params: query });

    return {
      list: response.data.data,
      ...response.data.meta
    };
  }

  async getMissionDetail({ code }) {
    const response = await this.axios.get(`/me/loyalty-missions/${code}`);

    return response.data.data;
  }

  async getItems({ page, pageSize, sort, status, token }) {
    const query = {
      page: page,
      page_size: pageSize
    };
    if (sort) query.sort = sort;
    if (status) query.status = status;
    if (token) query.token = token;
    const response = await this.axios.get('/me/loyalty-items', { params: query });

    return {
      list: response.data.data,
      ...response.data.meta
    };
  }

  async getItemDetail({ token }) {
    const items = await this.getItems({ token });
    if (items.list.length === 0) return null;

    return items.list.shift();
  }

  async getRedeemable({ page, pageSize, sort }) {
    const endpoint = '/loyalty-redeemables';
    const query = {
      page: page,
      page_size: pageSize
    };
    if (sort) query.sort = sort;
    const response = await this.axios.get(endpoint, { params: query });

    return {
      list: response.data.data,
      ...response.data.meta
    };
  }

  async getRedeemableDetail({ code }) {
    const response = await this.axios.get(`/loyalty-redeemables/${code}`);

    return response.data.data;
  }

  async redeemItem({ code, costId, returnItem }) {
    const endpoint = `/loyalty-redeemables/${code}/redeem`;
    const bodyParams = new URLSearchParams();
    if (costId) bodyParams.append('cost_id', costId);
    if (returnItem) bodyParams.append('return_item', returnItem);

    const response = await this.axios.post(endpoint, bodyParams);
    return response.data.data;
  }

  async getPointHistory({ page, pageSize, schemeId }) {
    const response = await this.axios.get('/me/loyalty-points', { params: {
      page: page,
      page_size: pageSize,
      scheme_id: schemeId
    } });

    return {
      list: response.data.data,
      ...response.data.meta
    };
  }

  async getNews({ page, pageSize, sort }) {
    const endpoint = `/newses`;
    const query = {
      page: page,
      page_size: pageSize
    };
    if (sort) query.sort = sort;

    const response = await this.axios.get(endpoint, { params: query });
    return {
      list: response.data.data,
      ...response.data.meta
    };
  }

  async getNewsDetail({ id }) {
    const endpoint = `/newses/${id}`;

    const response = await this.axios.get(endpoint);
    return response.data;
  }

  async getPromotions({ page, pageSize, sort }) {
    const query = {
      page: page,
      page_size: pageSize
    };
    if (sort) query.sort = sort;

    const response = await this.axios.get('/promotions', { params: query });
    return {
      list: response.data.data,
      ...response.data.meta
    };
  }

  async getPromotionDetail({ id }) {
    const response = await this.axios.get(`/promotions/${id}`);

    return response.data.data;
  }

  async patchPreference({ lang, timezone }) {
    const response = await this.axios.put('/me/preference', {
      language: lang,
      timezone: timezone
    });

    return response.data.data;
  }

  //----------------------------------------------------------------
  // membership

  async getMemberships() {
    const response = await this.axios.get(`/memberships`);

    return response.data;
  }

  async getUserMembershipRecord(reg_id) {
    const response = await this.axios.get(`/memberships/registration?reg_id=${reg_id}`);
    response.data.data.br_images = [];
    response.data.data.payment_images = [];
    response.data.data.student_card_images = [];
    response.data.data.consent_hkdc_tnc = '0';
    response.data.data.consent_join_member = '0';
    response.data.data.consent_transfer_data = '0';
    response.data.data.promo_code = '';
    response.data.data.referral_code = '';
    response.data.data.expected_grad_date = '';
    return {
      data: response.data.data,
      ...response.data.meta
    };
  }

  async getMembershipsRecord(type) {
    const postBodyParams = new FormData();
    postBodyParams.append('action', 'new');
    postBodyParams.append('membership_type', type);
    const response = await this.axios.post(`/memberships/register`, postBodyParams);

    return response.data;
  }

  async saveMembershipsRecord(formRecord) {
    formRecord.action = 'save';
    const postBodyParams = new FormData();
    Object.keys(formRecord).map((key) => {
      postBodyParams.append(key, formRecord[key]);
      return null;
    });
    const response = await this.axios.post(`/memberships/register`, postBodyParams);

    return response.data;
  }

  async renewMembershipsRecord(formRecord) {
    formRecord.action = 'renew';
    const postBodyParams = new FormData();
    Object.keys(formRecord).map((key) => {
      postBodyParams.append(key, formRecord[key]);
      return null;
    });
    const response = await this.axios.post(`/memberships/register`, postBodyParams);

    return response.data;
  }

  async submitMembershipsRecord(formRecord) {
    formRecord.action = 'submit';
    const postBodyParams = new FormData();
    Object.keys(formRecord).map((key) => {
      postBodyParams.append(key, formRecord[key]);
      return null;
    });
    const response = await this.axios.post(`/memberships/register`, postBodyParams);

    return response.data;
  }

  //----------------------------------------------------------------
  // Tier Benefits

  async getTierBenefits({ page, pageSize, sort }) {
    const endpoint = `/tier-benefits`;
    const query = {
      page: page,
      page_size: pageSize
    };
    if (sort) query.sort = sort;

    const response = await this.axios.get(endpoint, { params: query });
    return {
      list: response.data.data,
      ...response.data.meta
    };
  }

  async getTierBenefitsDetail({ id }) {
    const endpoint = `/tier-benefits/${id}`;

    const response = await this.axios.get(endpoint);
    return response.data;
  }

  async getIcons({ page, pageSize, sort }) {
    const endpoint = `/icons`;
    const query = {
      page: page,
      page_size: pageSize
    };
    if (sort) query.sort = sort;

    const response = await this.axios.get(endpoint, { params: query });
    return {
      list: response.data.data,
      ...response.data.meta
    };
  }
  //----------------------------------------------------------------
  // Notifications

  async getNotifications({ page, pageSize }) {
    const endpoint = `/notifications`;
    const query = {
      page: page,
      page_size: pageSize,
      type: 'all'
    };

    const response = await this.axios.get(endpoint, { params: query });

    return {
      list: response.data.data.results,
      ...response.data.meta
    };
  }

  async getUnreadNotifications() {
    const endpoint = `notifications/unread-count`;

    const response = await this.axios.get(endpoint);

    return {
      count: response.data.data.count,
      ...response.data.meta
    };
  }

  async setNotificationToRead({ readId }) {
    const endpoint = `/notifications/${readId}/read`;

    const response = await this.axios.post(endpoint);

    console.log('set notification to read', response.data);

    return response.data.data.success;
  }

  async requestUpdateIdentity({ email, countryCode, mobile, gRecaptchaResponse = null }) {
    const postData = {
      email,
      country_code: countryCode,
      mobile
    };
    if (gRecaptchaResponse) postData['g-recaptcha-response'] = gRecaptchaResponse;
    const response = await this.axios.post('/user/request-update-identity', postData);
    return response?.data?.data;
  }

  async updateRequestIdentity({ token, otp }) {
    const response = await this.axios.put('/user/update-request-identity', { token, otp });
    return response?.status === 202;
  }
}

const memberApi = new MemberApi();

export default memberApi;